import { useEffect, useState } from 'react'

import { useFormContext } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormField,
  FormItem,
  FormLabel,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SubmitButton,
} from '@fortem-monorepo/react-ui'
import { Swapagg } from '@fortem-monorepo/web-sdk'

import {
  addSwapaggSchema,
  addSwapaggSchema_ASWAP,
  addSwapaggSchema_FXFWD,
} from '@/components/forms/AddSwapaggForm/AddSwapaggSchema'
import { useOpsContext } from '@/pages/operations/ops-context.context'
import { useAddToSwapaggMutation } from '@/react-query/mutations/mappings'

const FXForwardFields = () => {
  const form = useFormContext<z.infer<typeof addSwapaggSchema_FXFWD>>()
  return (
    <>
      <FormField
        name="bank_id"
        control={form.control}
        render={() => {
          return (
            <FormItem>
              <FormLabel>Bank ID</FormLabel>
              <Input {...form.register('bank_id')} />
            </FormItem>
          )
        }}
      />
    </>
  )
}

const AssetSwapFields = () => {
  const form = useFormContext<z.infer<typeof addSwapaggSchema_ASWAP>>()
  return (
    <>
      <FormField
        name="bank_id"
        control={form.control}
        render={() => {
          return (
            <FormItem>
              <FormLabel>Bank ID</FormLabel>
              <Input {...form.register('bank_id')} />
            </FormItem>
          )
        }}
      />
    </>
  )
}

const options = [
  { label: 'Bond', value: 'bond' },
  { label: 'FX Forward', value: 'fxfwd' },
  { label: 'Asset Swap', value: 'aswap' },
] as const

type OptionValue = (typeof options)[number]['value']

// Map conditional schema to specific id_types with their
// corresponding react components to render dynamically.
const conditional_fields = {
  fxfwd: {
    schema: addSwapaggSchema_FXFWD,
    extraFields: <FXForwardFields />,
  },
  aswap: {
    schema: addSwapaggSchema_ASWAP,
    extraFields: <AssetSwapFields />,
  },
} as const

export interface AddSwapAggEntryModalProps {}

export const AddSwapAggEntryModal: React.FC<AddSwapAggEntryModalProps> = ({}) => {
  const [idType, setIdType] = useState<OptionValue>()

  const context = useOpsContext()
  const addToSwapaggMutation = useAddToSwapaggMutation()

  const form = useFormWithSchema(
    { mode: 'all' },
    conditional_fields?.[idType as keyof typeof conditional_fields]?.schema ?? addSwapaggSchema,
  )

  const {
    register,
    control,
    trigger,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, isValid },
  } = form

  const submitHandler = handleSubmit(async (data) => {
    const body: Swapagg.AddMappingToSwapaggBody = {
      input_type: idType!,
      fortemidentifier: data.fortem_id,
      trade_date: data.trade_date,
    }

    if (idType !== 'bond') {
      body.banktraderef = data.bank_id
    }

    await addToSwapaggMutation.mutateAsync(body)

    toast.success('Successfully added entry to SWAPAGG', {
      description: new Date().toISOString(),
    })
  })

  useEffect(() => {
    trigger()

    if (idType) {
      if (idType === 'bond') {
        setValue('bank_id', '', { shouldValidate: true })
      }
      setValue('id_type', idType, { shouldValidate: true })
    }
  }, [trigger, idType, context.newSwapAggEntry])

  return (
    <Dialog
      open={context.newSwapAggEntry !== null}
      onOpenChange={(e: any) => {
        if (!e) {
          context.setNewSwapAggEntry(null)
          setIdType('' as OptionValue)
          reset()
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <Form {...form}>
          <form onSubmit={submitHandler}>
            <DialogHeader>
              <DialogTitle>Add SWAPAGG Entry</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <FormField
                name="id_type"
                control={control}
                render={() => {
                  return (
                    <FormItem>
                      <FormLabel>ID Type</FormLabel>
                      <Select value={idType} onValueChange={(val: OptionValue) => setIdType(val)}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Select a Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {options.map((option) => (
                              <SelectItem value={option.value} key={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )
                }}
              />
              <FormField
                name="fortem_id"
                control={control}
                render={() => {
                  return (
                    <FormItem>
                      <FormLabel>Fortem ID</FormLabel>
                      <Input {...register('fortem_id')} />
                    </FormItem>
                  )
                }}
              />
              <FormField
                name="trade_date"
                control={control}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel className="">Trade Date</FormLabel>
                      <DatePicker
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) =>
                          form.setValue('trade_date', date ? toISO8601(date) : '', {
                            shouldValidate: true,
                          })
                        }
                      />
                    </FormItem>
                  )
                }}
              />
              {idType !== undefined &&
                conditional_fields[idType as keyof typeof conditional_fields]?.extraFields && (
                  <div className={'bg-body-background rounded-md p-4 space-y-3'}>
                    <div>
                      {conditional_fields[idType as keyof typeof conditional_fields].extraFields}
                    </div>
                  </div>
                )}
            </div>
            <DialogFooter>
              <Button
                disabled={isSubmitting}
                onClick={() => {
                  context.setNewSwapAggEntry(null)
                }}
                variant={'destructive'}
              >
                Cancel
              </Button>
              <SubmitButton loading={isSubmitting} disabled={isSubmitting || !isValid}>
                Add Entry
              </SubmitButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
